import type { FC } from 'react';
import { Typography } from '@mui/material';
import type { Dayjs } from 'dayjs';

import { StyledValidityContainer } from './MplTicketValidity.styled';

export interface IMplTicketValidityProps {
	validityLabel: string;
	validFrom: Dayjs;
	validUntil: Dayjs;
	/** Replaces the validFrom–validUntil range (e.g. Ticketshop Kurzstrecke stops). */
	value?: string;
	/** Optional extra line below the value (e.g. Kurzstrecke "valid until"). */
	secondaryValue?: string;
	/** Strikes through the value line — expired / cancelled tickets. */
	isVoided?: boolean;
	dataTestIdPrefix?: string;
}

/**
 * Validity row of a ticket card: the label plus the from–until date range, or
 * the `value` override (with its optional `secondaryValue` line) when the
 * ticket is not date-ranged.
 */
export const MplTicketValidity: FC<IMplTicketValidityProps> = ({
	validityLabel,
	validFrom,
	validUntil,
	value,
	secondaryValue,
	isVoided,
	dataTestIdPrefix = 'dticket',
}) => (
	<StyledValidityContainer>
		<Typography
			variant="b2"
			color="solutions.600"
			data-testid={`${dataTestIdPrefix}-validityLabel`}
		>
			{validityLabel}
		</Typography>

		<Typography
			variant="b1"
			color="primary.main"
			data-testid={`${dataTestIdPrefix}-validity-value`}
			sx={isVoided ? { textDecoration: 'line-through' } : undefined}
		>
			{value ??
				`${validFrom.format('DD.MM.YYYY')} - ${validUntil.format(
					'DD.MM.YYYY'
				)}`}
		</Typography>

		{secondaryValue && (
			<Typography
				variant="b1"
				color="primary.main"
				data-testid={`${dataTestIdPrefix}-validity-secondaryValue`}
			>
				{secondaryValue}
			</Typography>
		)}
	</StyledValidityContainer>
);
